/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Link } from 'react-router-dom';

export type RowMenuDropdownAction =
  | {
      type?: 'action';
      label: string;
      onClick?: (data: any) => void;
      to?: string;
      enabled?: boolean;
      disabled?: boolean;
      title?: string;
      element?: JSX.Element;
    }
  | {
      type: 'separator';
    };

export const rowMenuSeparator = (): RowMenuDropdownAction => ({ type: 'separator' });

export function rowMenuViewAction<TRow>(onClick: (row: TRow) => void): RowMenuDropdownAction {
  return { label: 'Visualizar', onClick };
}

export function rowMenuEditAction<TRow>(enabled: boolean, onClick: (row: TRow) => void): RowMenuDropdownAction {
  return { label: 'Editar', enabled, onClick };
}

export function rowMenuToggleActiveAction<TRow>(
  active: boolean | undefined,
  enabled: boolean,
  onClick: (row: TRow) => void,
): RowMenuDropdownAction {
  const label = active ? 'Desativar' : 'Ativar';
  return { label, enabled, onClick };
}

export function rowMenuRemoveAction<TRow>(enabled: boolean, onClick: (row: TRow) => void): RowMenuDropdownAction {
  return { label: 'Remover', enabled, onClick };
}

// `enabled: false` tira a opção do menu; o separador que fica sem nada entre dois grupos sai junto.
export function visibleRowMenuActions(actions: RowMenuDropdownAction[]) {
  const items: RowMenuDropdownAction[] = [];
  for (const action of actions) {
    const isSeparator = action.type === 'separator';
    if (!isSeparator && action.enabled === false) continue;
    if (isSeparator && (items.length === 0 || items[items.length - 1].type === 'separator')) continue;
    items.push(action);
  }
  if (items.length > 0 && items[items.length - 1].type === 'separator') items.pop();
  return items;
}

type RowMenuDropdownProps = {
  actions: RowMenuDropdownAction[];
  row?: any;
  dropdownWidth?: string;
  direction?: 'dropdown' | 'dropup';
  label?: string;
};

const RowMenuDropdown: React.FC<RowMenuDropdownProps> = ({
  label,
  row,
  actions,
  dropdownWidth,
  direction = 'dropdown',
}) => {
  const handleActionClick = (action: Extract<RowMenuDropdownAction, { type?: 'action' }>, e: React.MouseEvent) => {
    e.preventDefault();
    if (action.onClick) {
      action.onClick(row);
    }
  };

  const items = visibleRowMenuActions(actions);
  if (items.length === 0) return null;

  return (
    <div aria-expanded="false" aria-haspopup="true" className={direction}>
      {label ? (
        <button
          className="btn btn-secondary"
          data-bs-popper-config='{"strategy":"fixed"}'
          data-bs-toggle="dropdown"
          type="button"
        >
          Ações
        </button>
      ) : (
        <button
          aria-expanded="false"
          aria-label="Opções da linha"
          className="btn btn-icon btn-sm btn-light-secondary d-flex align-items-center justify-content-center"
          data-bs-popper-config='{"strategy":"fixed"}'
          data-bs-toggle="dropdown"
          type="button"
        >
          <svg
            className="bi bi-three-dots text-gray-600"
            fill="currentColor"
            height="19"
            viewBox="0 0 16 16"
            width="19"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3" />
          </svg>
        </button>
      )}

      <div className="dropdown-menu" style={{ width: dropdownWidth || 'auto' }}>
        <div className="m-2">
          <h6 className="dropdown-header ms-2 mt-2 fw-bold text-gray-800">Opções</h6>
          <div className="dropdown-divider mx-2" />
          <div className="mx-2 my-1">
            {items.map((action, idx) => {
              if ('type' in action && action.type === 'separator') {
                return <div key={idx} className="separator my-2" role="separator" />;
              }

              if (action.disabled) {
                return (
                  <div key={idx} title={action.title}>
                    <div
                      className="dropdown-item rounded text-muted d-flex flex-column"
                      style={{ cursor: 'not-allowed', opacity: 0.6 }}
                      aria-disabled="true"
                    >
                      {action.label}
                      {action.element}
                    </div>
                  </div>
                );
              }

              if (action.enabled === undefined || action.enabled) {
                if (action.to) {
                  return (
                    <div key={idx}>
                      <Link
                        className="dropdown-item rounded text-gray-700 d-flex flex-column text-decoration-none"
                        style={{ cursor: 'pointer' }}
                        to={action.to}
                      >
                        {action.label}
                        {action.element}
                      </Link>
                    </div>
                  );
                }

                return (
                  <div key={idx}>
                    <div
                      className="dropdown-item rounded text-gray-700 d-flex flex-column"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleActionClick(action, e)}
                    >
                      {action.label}
                      {action.element}
                    </div>
                  </div>
                );
              }

              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RowMenuDropdown;
